/**
 * Brand Constants - Single Source of Truth (P4 v4 / Session 24)
 *
 * This module now reads from `branding/brand.json` (the cross-stack source of truth).
 * Edit `branding/brand.json` to rebrand the entire application — backend, frontend,
 * marketing site, docs site all read from the same source.
 *
 * INTERNAL CODENAME vs EXTERNAL BRAND:
 *   - Codename "buildmaster" = legacy infrastructure ID (folders, repos, DBs).
 *     NEVER changes. It's like Microsoft's "Whistler" before "Windows XP".
 *   - External brand (e.g. "Injez") = customer-facing name. May change.
 *     Single source: branding/brand.json
 *
 * For per-tenant whitelabeling (future): when a tenant has a custom_brand
 * setting, override these defaults at the component level via authStore.
 */

// Import the single source of truth.
// Vite resolves JSON imports as typed objects.
import brandJson from '../../../branding/brand.json'

/**
 * BRAND - flat API shape consumed by all UI components.
 * Backwards-compatible with the pre-Session 25 shape.
 *
 * Any rebrand:  edit branding/brand.json → all consumers update on rebuild.
 */
export const BRAND = {
  // Product name (Latin script)
  name: brandJson.name.en,
  // Product name (Arabic script)
  nameAr: brandJson.name.ar,

  // Logo placeholder (single letter, used until a real logo asset is shipped)
  initial: brandJson.logo.initial,
  initialAr: brandJson.name.ar.charAt(0),

  // Tagline shown beneath the brand name
  tagline: brandJson.tagline.en,
  taglineAr: brandJson.tagline.ar,

  // Footer
  copyrightYear: Number(brandJson.copyright.year),
  copyrightSuffix: brandJson.copyright.suffixEn,
  copyrightSuffixAr: brandJson.copyright.suffixAr,

  // Logo gradient (Tailwind utility classes for `bg-gradient-to-br`)
  gradient: brandJson.gradients.primary,

  // Marketing site URL (for future links from the app)
  websiteUrl: `https://${brandJson.domains.main}`,

  // ─── Extended fields (new in Session 25) ──────────────────────────────────
  fullProductName: brandJson.name.fullProduct.en,
  fullProductNameAr: brandJson.name.fullProduct.ar,
  shortDescription: brandJson.shortDescription.en,
  shortDescriptionAr: brandJson.shortDescription.ar,
  domains: brandJson.domains,
  emailSupport: brandJson.supportContact.email,
  social: brandJson.social,
} as const

export type Brand = typeof BRAND

// Re-export the raw JSON for advanced consumers (marketing site, etc.)
export { brandJson as brandRaw }